import React, { useContext, useEffect, useMemo, useState } from "react"
import { RESERVATION_KINDS } from "~/constants"
import { NewReservationContext } from "~/contexts/admin/NewReservationContext"
import { AdminContext } from "~/contexts/AdminContext"
import { NewReservationAction } from "~/reducers/newReservation"
import { useCookies } from "react-cookie"
import isInvalidSendingMode from "~/components/Reservations/components/IsInvalidSendingMode"
import {
  COOKIE_SENDING_MODE_NEW_RESERVATION,
  groupOptions,
  reservationRecuringKindOptions,
} from "~/constants/NewReservation"
import SelectInput, { Option } from "~/components/ui/SelectInput"
import ReservationTypeSelector from "~/components/ui/ReservationTypeSelector"
import DiscountFormGroup from "~/components/Reservations/DiscountFormGroup"
import CheckboxWithLabelForForm from "~/components/ui/CheckboxWithLabelForForm"
import eSendingMode from "~/enums/eSendingMode"
import { sendingOptions } from "~/components/Reservations/sendingOptions"
import FeesAndTaxesSettings, {
  OptionKeys,
  ToggleFeesTypes,
} from "~/components/Admin/Reservations/NewReservationModal/FeesAndTaxesSettings"
import TakeDepositField from "~/components/Admin/Reservations/NewReservationModal/TakeDepositField"
import ILineItem from "~/interfaces/ILineItem"
import { LINE_ITEM_KINDS } from "~/constants/line_item_kinds"
import { v4 as uuidv4 } from "uuid"
import IAddOn from "~/interfaces/IAddOn"
import { returnActionTypeFeeByProp } from "./utils"
import ePaymentType from "~/enums/ePaymentType"
import { GROUP_PAYMENT_TYPES } from "~/constants/group_payment_types"
import ResponsivePricingSection from "./ResponsivePricingSection"
import { NewReservationProps } from "~/interfaces"

interface IComponentProps {
  isPaymentStage: boolean
  reservation: any // NewReservationProps
  setIsDiscountAdded: React.Dispatch<React.SetStateAction<boolean>>
  isShowingCc: boolean
  setIsShowingCc: React.Dispatch<React.SetStateAction<boolean>>
  isDiscountAdded: boolean
}

export const emptyLineItemAddonId = "change-me"

export const emptyLineItem: ILineItem = {
  id: "change-me",
  kind: LINE_ITEM_KINDS.BASIC,
  label: "",
  total_amount_in_cents: 0,
  unit_amount_in_cents: 0,
  occurrences: null,
  occurrence_unit: null,
  quantity: 0,
  tax_rate: 0,
  order_id: null,
  add_on_id: "change-me",
  reservation_id: null,
  parent_line_item_id: null,
  discount_id: null,
}

const NewReservationPayment = (props: IComponentProps) => {
  const { camp, sites, selectedSendingMode, setSelectedSendingMode } = useContext(AdminContext)
  const [cookies, setCookie] = useCookies([COOKIE_SENDING_MODE_NEW_RESERVATION])
  const [isEnabledFees, setIsEnabledFees] = useState<Partial<Record<keyof ToggleFeesTypes, boolean>>>({})
  const {
    lineItems,
    setLineItems,
    setUserLineItems,
    newReservation,
    order,
    dispatch,
    setIsAcceptingDeposit,
    isAcceptingDeposit,
    inputEmail,
    inputPhone,
    inputAdults,
    setInputAdults,
    inputChildren,
    setInputChildren,
    inputPets,
    setInputPets,
    setRecurringKind,
    recurringKind,
    setIsPayingSeparately,
    isPayingSeparately,
    isLoadingOrderPreview,
    selectedCampingStyle,
  } = useContext(NewReservationContext)

  const dropdownOptions: OptionKeys[] = [
    "taxRate",
    "creditCard",
    ...(newReservation.kind !== RESERVATION_KINDS.RECURRING
      ? ["holidayRate", "weekendFee", "passengerFee", "petsFee"]
      : []),
  ]

  const switchEnabledFees = (key: keyof ToggleFeesTypes, value: boolean) => {
    const newIsEnabledFees = { ...isEnabledFees, [key]: value }
    setIsEnabledFees(newIsEnabledFees)
    dispatch({
      type: returnActionTypeFeeByProp(key),
      payload: { [key]: value },
    })
    if (newReservation.kind === RESERVATION_KINDS.RECURRING) {
      dispatch({
        type: NewReservationAction.MODIFY_FEES_FOR_RECURRING,
        payload: { [key]: value },
      })
    }
  }

  const isGroup = newReservation.kind === RESERVATION_KINDS.GROUP
  const options = isGroup ? groupOptions : reservationRecuringKindOptions
  const selectedOptionValue = isGroup
    ? isPayingSeparately
      ? GROUP_PAYMENT_TYPES.SEPARATE
      : GROUP_PAYMENT_TYPES.TOGETHER
    : recurringKind
  const onChange = isGroup ? (value) => setIsPayingSeparately(value === GROUP_PAYMENT_TYPES.SEPARATE) : setRecurringKind

  const initializeEnabledFees = (newReservation: NewReservationProps) => {
    if (newReservation) {
      setIsEnabledFees({
        is_credit_card_fee_enabled: newReservation.is_credit_card_fee_enabled,
        is_pets_fee_enabled: newReservation.is_pets_fee_enabled,
        is_tax_enabled: newReservation.is_tax_enabled,
        is_passenger_fees_enabled: newReservation.is_passenger_fees_enabled,
        is_holiday_rate_enabled: newReservation.is_holiday_rate_enabled,
        is_weekend_fee_enabled: newReservation.is_weekend_fee_enabled,
      })
    }
  }

  useEffect(() => {
    if (newReservation) {
      if (newReservation.kind === RESERVATION_KINDS.RECURRING) {
        dispatch({ type: NewReservationAction.MODIFY_FEES_FOR_RECURRING })
      }
      initializeEnabledFees(newReservation)
    }
  }, [newReservation.kind, lineItems])

  const deposit_in_cents = useMemo(() => {
    if (!isAcceptingDeposit) return

    if (!order) {
      return
    }

    const defaultDeposit = selectedCampingStyle?.default_deposit_in_cents
    // Return undefined if there's no default deposit
    if (!defaultDeposit) {
      return
    }
    if (isAcceptingDeposit && defaultDeposit > order.total_in_cents) {
      return order.total_in_cents - 1
    }

    return defaultDeposit
  }, [order, isAcceptingDeposit, selectedCampingStyle?.default_deposit_in_cents])

  const handleDepositButton = () => {
    setIsAcceptingDeposit(!isAcceptingDeposit)
    dispatch({
      type: NewReservationAction.UPDATE_DEPOSIT_AMOUNT,
      payload: {
        deposit_in_cents: (deposit_in_cents ?? 0) / 100,
      },
    })
  }

  const addonToLineItem = (product: IAddOn, lineItem: Partial<ILineItem>): ILineItem => {
    return {
      id: product.id,
      kind: lineItem?.kind,
      label: product.label,
      total_amount_in_cents: product.amount_in_cents,
      unit_amount_in_cents: product.amount_in_cents,
      occurrences: lineItem?.occurrences,
      occurrence_unit: lineItem?.occurrence_unit,
      quantity: 1,
      tax_rate: lineItem?.tax_rate,
      order_id: lineItem?.order_id,
      add_on_id: product.id,
      reservation_id: null,
      parent_line_item_id: lineItem?.parent_line_item_id,
      discount_id: lineItem?.discount_id,
    }
  }

  const selectProduct = (product: IAddOn, row: ILineItem) => {
    const mLineItems = [...lineItems]
    const addonIdx = mLineItems.findIndex((li) => li.add_on_id === row.add_on_id)
    mLineItems[addonIdx] = addonToLineItem(product, mLineItems[addonIdx])
    setUserLineItems(mLineItems)
  }

  const createNewItem = (name: string, id?: string) => {
    let mLineItems = [...lineItems]
    const addonIdx = mLineItems.findIndex((li) => id && li.add_on_id === id)
    const newLineItem: ILineItem = {
      ...emptyLineItem,
      kind: LINE_ITEM_KINDS.BASIC,
      add_on_id: uuidv4(),
      label: name,
      quantity: 1,
      id: null,
      unit_amount_in_cents: 100,
      total_amount_in_cents: 100,
    }
    if (addonIdx >= 0) {
      mLineItems[addonIdx] = newLineItem
    }
    setUserLineItems(mLineItems)
  }

  const deleteAddon = (lineItem: ILineItem, isAddOn: boolean, isNew: boolean) => {
    let mLineItems = [...lineItems]
    if (lineItem.add_on_id === emptyLineItemAddonId) {
      const itemIdx = mLineItems.findIndex((item) => item?.add_on_id === emptyLineItemAddonId)
      mLineItems.splice(itemIdx, 1)
      setLineItems(mLineItems)
    } else if (isNew) {
      mLineItems = mLineItems.filter((item) => item.label !== lineItem.label)
    } else if (isAddOn) {
      mLineItems = mLineItems.filter((item) => item?.add_on_id !== lineItem.add_on_id)
    }
    setUserLineItems(mLineItems)
  }

  useEffect(() => {
    if (
      newReservation &&
      [ePaymentType.CARD_READER, ePaymentType.CARD_INPUT, ePaymentType.LINK].includes(newReservation.paymentMethod)
    ) {
      dispatch({
        type: returnActionTypeFeeByProp("is_credit_card_fee_enabled"),
        payload: { ["is_credit_card_fee_enabled"]: true },
      })
    } else {
      dispatch({
        type: returnActionTypeFeeByProp("is_credit_card_fee_enabled"),
        payload: { ["is_credit_card_fee_enabled"]: false },
      })
    }
  }, [newReservation.paymentMethod])

  return (
    <div className="h-full px-1">
      {(newReservation.kind === RESERVATION_KINDS.RECURRING ||
        newReservation.kind === RESERVATION_KINDS.SEASONAL ||
        newReservation.kind === RESERVATION_KINDS.GROUP) && (
          <div className="mb-8">
            <h4 className="text-xl font-semibold mb-2.5">
              {newReservation.kind === RESERVATION_KINDS.GROUP ? "Group" : "Recurring"} settings
            </h4>
            <div>
              <div className="hidden sm:block">
                <ReservationTypeSelector
                  options={options}
                  selectedOption={selectedOptionValue}
                  onChange={onChange}
                  buttonWidth="w-48"
                  isLoading={isLoadingOrderPreview}
                />
              </div>
              <div className="sm:hidden w-full mt-1.5 mb-5">
                <SelectInput
                  options={options}
                  selected={options.find((option) => option.value === selectedOptionValue)}
                  setSelected={(option: Option) => onChange(option.value)}
                />
              </div>
            </div>
          </div>
        )}

      <ResponsivePricingSection
        sites={sites}
        camp={camp}
        selectProduct={selectProduct}
        createNewItem={createNewItem}
        deleteAddon={deleteAddon}
        dispatch={dispatch}
        isLoadingOrderPreview={isLoadingOrderPreview}
        isPayingSeparately={isPayingSeparately}
        setLineItems={setLineItems}
      />

      {!(newReservation.kind === RESERVATION_KINDS.GROUP && isPayingSeparately) && (
        <div className="flex flex-col items-start gap-y-1.5 pt-4">
          <h4 className="text-xl font-semibold">Invoice Adjustments</h4>
          {(camp.discounts.length >= 0 || selectedCampingStyle.discounts.length > 0) && props.isDiscountAdded && selectedCampingStyle && (
            <div className="w-full md:max-w-96 -mb-1.5">
              <div className="inline font-bold text-md text-gray-900">Discount</div>
              <DiscountFormGroup
                camp={camp}
                campingStyle={selectedCampingStyle}
                newReservation={newReservation}
                newReservationDispatch={dispatch}
              />
            </div>
          )}
          {(camp.discounts.length > 0 || selectedCampingStyle.discounts.length > 0) && (
            <button
              className="!px-2 !py-1 -ml-2 cursor-pointer text-green-600 disabled-add-line-item flex items-center"
              onClick={() => {
                if (props.isDiscountAdded) {
                  props.setIsDiscountAdded(false)
                  dispatch({
                    type: NewReservationAction.UPDATE_DISCOUNT_NAME,
                    payload: { discount_name: undefined },
                  })
                } else {
                  props.setIsDiscountAdded(true)
                }
              }}
            >
              {props.isDiscountAdded ? "Remove discount" : "Add discount"}
            </button>
          )}
          <TakeDepositField
            reservation={{ deposit_in_cents: deposit_in_cents }}
            orderUnpaidSubTotal={((order?.subtotal_in_cents ?? 1) - 1) / 100}
            isAcceptingDeposit={isAcceptingDeposit}
            handleDepositButton={handleDepositButton}
            dispatch={dispatch}
            className="w-full md:max-w-96"
            buttonClassName="-ml-2"
          />
          <FeesAndTaxesSettings
            enabledFees={isEnabledFees}
            options={dropdownOptions}
            setValue={(key, value) => switchEnabledFees(key, value)}
            campingStyle={selectedCampingStyle}
            isGroup={newReservation.kind === RESERVATION_KINDS.GROUP}
            className="!justify-start !-ml-2 hover:bg-none"
            paymentMethod={newReservation.paymentMethod}
            isAcceptingDeposit={isAcceptingDeposit}
            placement="left"
          />
        </div>
      )}

      {/* Notification settings */}
      <div className="w-72 mt-7">
        <h4 className="text-xl font-semibold mb-2.5">Notification settings</h4>
        {newReservation.kind !== "blocked" && (
          <div className="md:mr-4 flex flex-col items-left mb-2.5 md:mb-0 w-full">
            <SelectInput
              name="sendingOptions"
              large
              buttonSize="large"
              width="w-72"
              isBottomPlacement
              options={sendingOptions}
              selected={sendingOptions.find((o) => o.value === selectedSendingMode)}
              setSelected={(o: Option) => {
                setSelectedSendingMode(o.value)
                setCookie(COOKIE_SENDING_MODE_NEW_RESERVATION, o.value)
              }}
              warningText={
                isInvalidSendingMode(inputEmail, inputPhone, selectedSendingMode) && (
                  <div className="text-xs text-yellow-600">
                    {selectedSendingMode === eSendingMode.EMAIL
                      ? "Won't send without a valid email"
                      : selectedSendingMode === eSendingMode.TEXT
                        ? "Won't send without a valid phone number"
                        : "Won't send without a valid email & phone number"}
                  </div>
                )
              }
            />
            {(selectedSendingMode === eSendingMode.EMAIL || selectedSendingMode === eSendingMode.EMAIL_AND_TEXT) && (
              <div className="flex items-center md:mr-3 mb-2 md:mb-0 mt-3 pb-2">
                <CheckboxWithLabelForForm
                  label={`cc ${camp.email}`}
                  value={props.isShowingCc}
                  setValue={props.setIsShowingCc}
                  labelClasses="text-sm whitespace-nowrap"
                />
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

export default NewReservationPayment